import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { SCREENSHOT_MAX_BYTES, canInAnyScope } from '@testflow/shared';
import { useCallback, useEffect, useState } from 'react';
import type { ClipboardEvent, DragEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { E2EBadge, IdLabel } from '../../components/atoms.js';
import { ImageLightbox } from '../../components/image-lightbox.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { ApiError } from '../../lib/api.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { dataKindsApi, kindLabel } from '../../lib/data-kinds-api.js';
import { defectsApi } from '../../lib/defects-api.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { type RunSubmitBody, runsApi } from '../../lib/runs-api.js';
import { ChainBar } from './chain-bar.js';
import { ExecutionComments } from './execution-comments.js';
import { NotedDefectsChoice } from './noted-defects-choice.js';
import { StepChecklist } from './step-checklist.js';

// Design-Spec §3.3 Hauptbereich: ID + E2E-Kontext + Testdaten-Auswahl ▾ (EXE-7) · Titel ·
// Vorbedingung/Erwartet als Chips · Schritte-Tabelle (Schritt-Texte hier nicht editierbar, anders
// als in der Katalog-StepTable; #208: Haken und Defect je Schritt, StepChecklist) ·
// Screenshot-Dropzone (Paste/Drag, EXE-6) · Aktionsleiste mit
// "✓ Passt", "✕ Fehler melden" (Dialog), "Blockiert", "Überspringen". #185: keine Tastatur-
// Abkürzung mehr — in der ETO-Schulung wurden Tests versehentlich per Enter als bestanden gebucht.
// Nach jedem Submit (außer Überspringen) → runsApi.submitRun, danach IMMER `onSubmitted()`
// (Auto-Sprung EXE-1 — der Aufrufer in my-tests-page.tsx berechnet daraus den nächsten
// Playlist-Eintrag). Die Bild-Zone lebt als gemeinsamer State auf Panel-Ebene (nicht dupliziert
// im Fehler-Dialog): der Dialog ist kein Portal, Paste-Events innerhalb des Dialogs bubblen also
// zum umschließenden `onPaste`-Handler durch. Der lokale Panel-State (Testdaten-Auswahl,
// Screenshots, Fehler-Dialog) muss bei jedem Testfallwechsel zurückgesetzt werden — der Aufrufer
// erzwingt das über `key={playlistEntryKey(...)}` (Remount statt Reset-Effekt) — seit Task 3 am
// PAAR (Testfall, Kette), damit auch der Wechsel der Kette DESSELBEN Testfalls zurücksetzt.

function ScreenshotZone({
  screenshots,
  error,
  onDrop,
  onRemove,
}: {
  screenshots: string[];
  error: string | null;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState<number | null>(null);
  // Stabil halten: der Effekt in ImageLightbox haengt an onClose und ruft dort focus() (#37).
  const closeZoom = useCallback(() => setZoom(null), []);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="rounded-card border border-dashed border-[var(--border)] p-3 text-center text-xs text-text-muted"
    >
      <p>{t('exec.screenshotHint')}</p>
      {error && <p className="mt-1 text-status-fail">{error}</p>}
      {screenshots.length > 0 && (
        <>
          <p className="mt-1">{t('exec.screenshotsAttached', { count: screenshots.length })}</p>
          <ul className="mt-2 flex flex-wrap justify-center gap-2">
            {screenshots.map((src, index) => (
              <li key={`${index}-${src.length}`} className="relative">
                <button type="button" onClick={() => setZoom(index)} className="block">
                  {/* alt statt leer: die Schaltflaeche hat sonst keinen zugaenglichen Namen
                      (Review-Befund 1) — der Bildtext traegt den Button-Namen mit. */}
                  <img
                    src={src}
                    alt={`${t('exec.screenshot')} ${index + 1}`}
                    className="h-12 w-12 rounded-btn object-cover"
                  />
                </button>
                <button
                  type="button"
                  // Kein stopPropagation noetig: Bild-Button und dieses X sind Geschwister im
                  // <li>, der Klick laeuft also gar nicht durch den Bild-Button. Dass das
                  // Entfernen die Lightbox nicht oeffnet, haelt stattdessen ein Test fest — der
                  // wuerde anschlagen, falls das X spaeter ins Bild wandert.
                  onClick={() => onRemove(index)}
                  aria-label={t('exec.removeScreenshot')}
                  title={t('exec.removeScreenshot')}
                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-status-fail text-[10px] text-white"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          {zoom !== null && (
            <ImageLightbox
              images={screenshots}
              startIndex={zoom}
              // screenshotHint ist der Paste-Bedienhinweis, nicht die Bildbeschreibung
              // (Review-Befund 2) — die Lightbox nutzt `label` als aria-label UND als alt.
              label={t('exec.screenshot')}
              onClose={closeZoom}
            />
          )}
        </>
      )}
    </div>
  );
}

// #208 Nachtrag: Antworten des Servers, nach denen die Liste notierter Fehler veraltet ist (ein
// notierter Fehler wurde inzwischen verknüpft, gelöscht oder gehört nicht mehr zu dieser Durchführung).
function isLinkedDefectError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.message === 'linked_defect_not_noted' || err.message === 'linked_defect_changed')
  );
}

// =================================================================================================
// Task 9 (#113, A3/A6): DER KETTENKONTEXT DES LAUFS.
//
// Drei Entscheidungen tragen diesen Teil des Panels:
//
// 1. DIE KETTE WIRD IMMER MITGESCHICKT. Seit Task 5 stellt der Server die Kettenzugehörigkeit
//    selbst fest: Liegt der Testfall in mindestens einer in-scope-Kette dieses Plans und kommt der
//    Lauf ohne `e2e_id`, antwortet er mit 422 `e2e_id_required` — auch beim ganz normalen grünen
//    Lauf. Jeder der drei Absendewege (`handlePass`, `handleBlocked`, `submitError`) trägt die
//    Kette deshalb im Rumpf; kettenlos heißt `null`, nicht „Feld weglassen".
//
// 2. DIE BAUMSICHT LEITET IHRE KETTE AUS DER MITGLIEDSCHAFT AB. Der Baum ist die katalogorientierte
//    Sicht und wählt laut Task-3-Entscheidung dauerhaft `e2eId = null` — ein von dort gestartetes
//    Kettenglied hätte sonst keine Kette zu nennen und liefe in genau diese 422. Bei GENAU EINER
//    Mitgliedschaft ist die Wahl eindeutig und wird abgeleitet; bei MEHREREN wählt der Tester
//    (A6: „Der Tester wählt damit den Kettenkontext; der Lauf wird DIESER Kette zugeschrieben") —
//    solange er nicht gewählt hat, ist das Absenden gesperrt, mit benanntem Grund. Bewusst NICHT
//    vorausgewählt: eine stille Vorauswahl schriebe die Belegdaten einer Kette zu, die der Tester
//    nie angesehen hat, und beide Ketteneinträge sähen dabei gleich aus.
//
// 3. „HAT MEIN GLIED EINEN NACHFOLGER?" KOMMT AUS DER KETTENSICHT — keine dritte Wahrheit. Der
//    Server entscheidet es mit `hasSuccessorInScope` (Task 5); die Kettensicht (Task 6) liefert
//    dieselbe Lage als bereits sortierte, auf den Plan-Scope beschränkte `links`-Liste. Das Panel
//    liest daraus nur die eigene POSITION (letztes Glied oder nicht) und rechnet nichts über
//    `sequence` nach — Lücken durch abgewählte Glieder machen `sequence ± 1` unbrauchbar. Es ist
//    exakt dieselbe Abfrage wie in der Kettenleiste (`['e2e-chain', …]`), react-query teilt den
//    Cache: kein zweiter Abruf. Die Oberfläche bleibt dabei die Bequemlichkeit, nicht die Grenze —
//    durchgesetzt wird die Pflicht serverseitig (400 `result_description_required`), und dessen
//    Antwort wird hier ebenso angezeigt wie die Sperre (409) und die fehlende Kette (422).
// =================================================================================================

export function ExecutionPanel({
  projectId,
  planId,
  testCaseId,
  e2eId: e2eIdProp,
  chainOrderEnforced = true,
  onSubmitted,
}: {
  projectId: string;
  planId: string;
  testCaseId: string | null;
  // Task 3 (A6): Der Kettenkontext des gewählten Playlist-Eintrags — ein Testfall kann in mehreren
  // Ketten liegen und wird je Kette einzeln getestet. null = kettenloser Eintrag ODER Auswahl aus
  // der Baum-Ansicht, die keine Kette kennt; welcher der beiden Fälle vorliegt, entscheidet erst
  // die Mitgliedschaftsliste des Testfalls (s. `pickedE2eId`/`effectiveE2eId` unten).
  e2eId: string | null;
  // #127: false = der Plan hat scope_mode 'processes', also keine Ketten-Reihenfolge. Das Panel
  // blendet den Kettenkontext dann ganz aus (Offene Frage 1): keine Kettenleiste, keine
  // Kettenwahl, kein Übergabefeld, und jeder Lauf geht mit e2e_id = null raus — genau das, was der
  // Server in so einem Plan ohnehin speichert. Der Katalog-Hinweis (E2E-Badge) bleibt: er sagt, dass
  // der Testfall in einer Kette liegt, und ist damit eine Katalogtatsache, keine Planregel.
  chainOrderEnforced?: boolean;
  onSubmitted: () => void;
}) {
  const { t, i18n } = useTranslation();
  const e2eId = chainOrderEnforced ? e2eIdProp : null;
  const queryClient = useQueryClient();

  const [selectedTestDataIds, setSelectedTestDataIds] = useState<string[]>([]);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [failedStepId, setFailedStepId] = useState('');
  const [resultDescription, setResultDescription] = useState('');
  const [severityId, setSeverityId] = useState(''); // #90: '' = kein Vorschlag → sendet null
  // #186: die Pflichtwahl des Fehler-Dialogs (zwei Schaltflächen statt Ja/Nein, doppelte
  // Verneinung). Intern bleibt 'yes' = „Testfall nicht bestanden" (der Fehler hält die Kette an)
  // und 'no' = „Testfall bestanden" (Fehler gemeldet, Kette läuft weiter) — so bleibt der
  // API-Vertrag (`stops_chain`) unberührt. null = noch nicht gewählt, bewusst KEINE Vorauswahl:
  // ein Standardwert würde dem Tester die Entscheidung abnehmen.
  const [stopsChain, setStopsChain] = useState<'yes' | 'no' | null>(null);
  // Task 9 (A3): EIGENER Zustand für den Übergabetext des grünen Laufs — bewusst NICHT der
  // vorhandene `resultDescription` des Fehler-Dialogs, obwohl beide in dieselbe Spalte
  // (`test_runs.result_description`) münden. Zwei Gründe: Der Dialog-Zustand überlebt sein
  // Schließen (deshalb setzt das Öffnen `severityId` zurück, s. u.), ein abgebrochener Fehlertext
  // stünde also anschließend als Übergabe im grünen Lauf — und umgekehrt trüge der Fehler-Dialog
  // die Belegnummer als Fehlerbeschreibung. Fachlich sind es ohnehin zwei verschiedene Sätze:
  // „was ist schiefgegangen" und „was der Nächste braucht". Ein Lauf trägt immer nur einen davon,
  // weil er genau ein Ergebnis hat.
  const [chainResult, setChainResult] = useState('');
  // Task 9 (A6): die vom Tester GEWÄHLTE Kette, wenn die Auswahl selbst keine nennt (Baumsicht) und
  // der Testfall in mehreren Ketten liegt. '' = noch nicht gewählt.
  const [pickedE2eId, setPickedE2eId] = useState('');
  // #208 Nachtrag: Auswahl im Fehler-Dialog, wenn es notierte Fehler gibt. Geführt werden die
  // ABGEWÄHLTEN — Vorauswahl ist „alle", auch für einen Defect, der erst nach dem Öffnen geladen wird.
  const [unlinkedIds, setUnlinkedIds] = useState<string[]>([]);
  // „Zusätzlich neuen Fehler melden" — Vorauswahl aus (nur relevant mit notierten Fehlern).
  const [createNew, setCreateNew] = useState(false);

  // A5: Die Kettenleiste kennt selbst keine Rollen — das Freigaberecht rechnet ihr Aufrufer einmal
  // aus und reicht es durch (dasselbe Muster wie `canViewDefect` an der Playlist). `projectId` wird
  // ausdrücklich übergeben, damit auf GENAU das Projekt gescopt wird, für das dieses Panel rendert.
  const { user } = useScopedUser(projectId);
  const canRelease = user ? canInAnyScope(user, 'e2e.release') : false;

  const tcQuery = useQuery({
    queryKey: ['tc', projectId, testCaseId],
    queryFn: () => catalogApi.getTestCase(projectId, testCaseId ?? ''),
    enabled: Boolean(testCaseId),
  });

  const testDataQuery = useQuery({
    queryKey: ['testdata', projectId],
    queryFn: () => catalogApi.listTestData(projectId),
    enabled: Boolean(testCaseId),
  });

  // EXE-7 (Task 8b): Testdaten kennen nur noch data_kind_id — die Bezeichnung wird hier
  // gegen die Datenarten-Liste des Projekts aufgeloest (dieselbe Query wie im Katalog-Tab).
  const dataKindsQuery = useQuery({
    queryKey: ['data-kinds', projectId],
    queryFn: () => dataKindsApi.list(projectId),
    enabled: Boolean(testCaseId),
  });

  // #90: Severity-Optionen für den Fehler-Dialog. `report.read` genügt (der Tester hat es,
  // packages/shared/src/permissions.ts) — derselbe Endpunkt/Query-Key wie Board/Anlege-Dialog,
  // also geteilter Cache.
  const defectConfigQuery = useQuery({
    queryKey: ['defect-config', projectId],
    queryFn: () => defectsApi.config(projectId),
    enabled: Boolean(testCaseId),
  });

  // #208 Nachtrag: notierte Fehler dieser Durchführung — dieselbe Abfrage (gleicher Schlüssel) wie
  // die Markierung in der Schritt-Tabelle, also geteilter Cache. 403 (ohne run.execute) und
  // Ladefehler heißen hier: keine Auswahl, der Fehler-Dialog bleibt wie heute.
  const notedQuery = useQuery({
    queryKey: ['noted-step-defects', projectId, planId, testCaseId],
    queryFn: () => runsApi.listNotedStepDefects(projectId, planId, testCaseId ?? ''),
    enabled: Boolean(testCaseId) && Boolean(planId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
  const notedDefects = notedQuery.data?.defects ?? [];
  const hasNoted = notedDefects.length > 0;
  // #208 (Final-Review 2): Solange die Liste lädt oder mit einem Fehler außer 403 scheiterte, wäre der
  // Dialog „wie ohne notierte Fehler" — und ein Lauf ohne Links bucht den Haken-Stand weg. Deshalb
  // bleibt Speichern gesperrt, bis die Liste da ist (oder der Nutzer erfolgreich neu lädt). 403 =
  // kein `run.execute`, dann gibt es keine notierten Fehler: Dialog wie heute.
  const notedLoading = notedQuery.isLoading;
  const notedFailed =
    notedQuery.isError &&
    !(notedQuery.error instanceof ApiError && notedQuery.error.status === 403);
  const notedUnavailable = notedLoading || notedFailed;
  // #208 (Final-Review 2): abgewählte Ids, die nicht mehr in der Liste stehen (verknüpft oder weg),
  // fliegen raus — sonst sammeln sich veraltete Ids über mehrere Läufe an.
  const notedIdsKey = notedQuery.data
    ? notedQuery.data.defects.map((d) => d.defect_id).join('|')
    : null;
  useEffect(() => {
    if (notedIdsKey === null) return;
    const present = notedIdsKey.split('|');
    setUnlinkedIds((prev) => {
      const next = prev.filter((id) => present.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [notedIdsKey]);
  const linkedIds = notedDefects.map((d) => d.defect_id).filter((id) => !unlinkedIds.includes(id));
  // Entscheidung 4: Mit mindestens einem angehakten notierten Fehler ist das Ergebnis fest „nicht
  // bestanden" — die Bestanden-Wahl entfällt, intern gilt `stopsChain = 'yes'` (sonst sperrte
  // `errorSaveBlocked`). Ohne Haken entscheidet wie heute der Tester.
  const linkedFixed = hasNoted && linkedIds.length > 0;
  // Der Teil „neuer Fehler" (Schritt, Severity): ohne notierte Fehler immer (heutiger Dialog), mit
  // notierten Fehlern nur, wenn „Zusätzlich neuen Fehler melden" angehakt ist.
  const newPartActive = !hasNoted || createNew;
  // Entscheidung 3: Nicht bestanden ohne Defect gibt es nicht — ohne Haken und ohne „neu" kein Lauf.
  const nothingChosen = hasNoted && linkedIds.length === 0 && !createNew;

  // ===============================================================================================
  // Task 9: Kettenkontext bestimmen (s. Entscheidung 2 im Kopf der Datei).
  // ===============================================================================================
  // Kunden-weite Mitgliedschaften des Testfalls. Sie decken sich mit der Menge, aus der der Server
  // `e2e_id_required` ableitet, solange der Testfall im Plan in-scope ist — und ist er das nicht,
  // scheitert der Lauf ohnehin vorher an `plan_item_not_found`.
  const memberships = (chainOrderEnforced ? (tcQuery.data?.e2e_memberships ?? []) : [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence || a.e2e_id.localeCompare(b.e2e_id));
  const inChain = Boolean(e2eId) || memberships.length > 0;
  // Der Vorrang ist bewusst: Nennt die Auswahl eine Kette (Playlist, ein Eintrag JE Kette), gilt
  // sie. Sonst zählt die einzige Mitgliedschaft, sonst die Wahl des Testers. `pickedE2eId` wird
  // gegen die Mitgliedschaften geprüft, damit eine Wahl nicht überlebt, wenn der Testfall
  // inzwischen aus der Kette genommen wurde.
  const validPick = memberships.some((m) => m.e2e_id === pickedE2eId) ? pickedE2eId : '';
  const effectiveE2eId =
    e2eId ?? (memberships.length === 1 ? (memberships[0]?.e2e_id ?? null) : validPick || null);
  const needsChainPick = inChain && !effectiveE2eId;
  // Review-Befund Important 1 (Task 9): OB die Auswahlliste ERSCHEINT, ist eine andere Frage als
  // OB sie SPERRT. `needsChainPick` beantwortet nur die zweite — sie wird `false`, sobald eine
  // gültige Wahl vorliegt, also mit dem ERSTEN Klick. Hinge die Liste selbst daran, verschwände sie
  // in genau diesem Moment: Wählt der Tester die falsche Kette, gibt es danach keinen Weg mehr,
  // sie zu ändern — der Lauf samt Übergabetext würde der falschen Kette zugeschrieben, korrigierbar
  // nur über den Umweg „anderen Testfall wählen und zurück" (Remount per `key`). Deshalb bleibt die
  // Liste sichtbar, SOLANGE DIE AUSWAHL SELBST KEINE KETTE NENNT — unabhängig davon, ob der Tester
  // bereits gewählt hat: `e2eId === null` (Baumsicht) UND `memberships.length > 1` (überhaupt eine
  // Wahl zu treffen). `value={validPick}` bleibt an der geprüften Wahl, die SPERRE bleibt an
  // `needsChainPick` — ändert der Tester die Wahl, folgen Kettenleiste und Nachfolger-Urteil sofort
  // mit, weil beide aus `effectiveE2eId` (und damit `pickedE2eId`) abgeleitet sind.
  const showChainPick = e2eId === null && memberships.length > 1;

  // Namen für die Auswahl. Eine Abfrage JE Mitgliedschaft, und zwar dieselbe wie die der
  // Kettenleiste: Die gewählte Kette ist damit bereits geladen, wenn die Leiste aufgeht (geteilter
  // react-query-Cache). Die Alternative — die kundenweite Katalogliste `listE2e` — wäre eine zweite
  // Quelle für dieselbe Sache und wüsste nichts über den Plan-Scope. An `showChainPick` gekoppelt,
  // nicht an `needsChainPick`: sonst verschwänden die Namen aus dem Cache-Zugriff, sobald der
  // Tester gewählt hat, und die weiterhin sichtbare Liste zeigte nur noch `catalog.loading`.
  const chainNameQueries = useQueries({
    queries: (showChainPick ? memberships : []).map((m) => ({
      queryKey: ['e2e-chain', projectId, planId, m.e2e_id],
      queryFn: () => runsApi.chain(projectId, planId, m.e2e_id),
      enabled: Boolean(projectId) && Boolean(planId),
    })),
  });

  // A3: „hat mein Glied einen Nachfolger?" — allein aus der Kettensicht (s. Entscheidung 3).
  const chainQuery = useQuery({
    queryKey: ['e2e-chain', projectId, planId, effectiveE2eId],
    queryFn: () => runsApi.chain(projectId, planId, effectiveE2eId ?? ''),
    enabled: Boolean(projectId) && Boolean(planId) && Boolean(effectiveE2eId),
  });
  const chainLinks = chainQuery.data?.links;
  const ownIndex = chainLinks?.findIndex((l) => l.test_case_id === testCaseId) ?? -1;
  // Solange die Kettensicht nicht geladen ist, ist die Antwort unbekannt — dann sperrt die
  // Oberfläche NICHT. Die Pflicht selbst hängt nicht an ihr: der Server weist den Lauf sonst mit
  // 400 `result_description_required` ab, und dieser Fehler wird unten angezeigt.
  const hasSuccessor = chainLinks ? ownIndex >= 0 && ownIndex < chainLinks.length - 1 : false;
  const needsResultText = Boolean(effectiveE2eId) && hasSuccessor && !chainResult.trim();
  // Review-Befund Important 2 (Task 10): DIE VORGÄNGER-SPERRE, DIE HIER FEHLTE.
  //
  // Die Playlist sperrt die gesperrte Karte (Maus + Tastatur), der Deeplink meidet sie — das Panel
  // selbst kannte bis hierher nur `needsChainPick` und `needsResultText`. Wer trotzdem hier landet
  // (Deeplink auf einen Testfall, der NUR gesperrt in der Playlist steht; Baumsicht; ein veralteter
  // Playlist-Stand), füllte aus, drückte einen Ergebnis-Knopf und bekam 409
  // `predecessor_not_done` — ein Fehler, den er nicht verursacht hat.
  //
  // Das Urteil kommt aus der Kettensicht, die hier ohnehin geladen ist (A3), und ist dort seit dem
  // Review vom Anzeige-`state` entkoppelt (plan-status.ts) — es gilt also auch am Glied mit eigenem
  // Fehler- oder Retest-Lauf. Solange die Kettensicht nicht geladen ist, sperrt die Oberfläche
  // NICHT (dieselbe Zurückhaltung wie bei `hasSuccessor`): die Pflicht hängt nicht an ihr, der
  // Server weist den Lauf sonst ab und der Fehler wird unten angezeigt.
  //
  // Whole-Branch-Review, Important 2: HIER wird `blocked_by` ROH gelesen — ohne die Anzeige-
  // Rücksicht, die Playlist-Karte und Kettenleiste nehmen (`isLocked` bzw. `waitingFor`). Der
  // Unterschied ist genau die Konstellation des Befunds: ein bestandenes Glied 3, dessen Glied 2
  // nachträglich auf `error` gefallen ist, ist eine ERLEDIGTE Karte (nicht ausgegraut, anklickbar)
  // — ein WIEDERHOLUNGSLAUF daran weist der Server aber mit 409 ab. Deshalb sperrt das Panel ihn
  // und nennt den Grund, statt den Tester in den Fehler laufen zu lassen.
  const predecessorBlocked = (ownIndex >= 0 ? chainLinks?.[ownIndex]?.blocked_by : null) ?? null;
  // Ein ausgegrauter Knopf allein sagt nichts — der Grund steht daneben und hängt per
  // aria-describedby am Knopf.
  const passBlockedReason = needsChainPick
    ? t('exec.chain.pickChainRequired')
    : predecessorBlocked
      ? t('exec.chain.predecessorRequired', {
          name: predecessorBlocked.tester_name ?? t('exec.chain.unknownTester'),
          sequence: predecessorBlocked.sequence,
          title: pickLang(
            i18n.resolvedLanguage,
            predecessorBlocked.title_de,
            predecessorBlocked.title_en,
          ),
        })
      : needsResultText
        ? t('exec.chain.resultRequired')
        : null;
  // Die Vorgänger-Sperre gilt für ALLE DREI Ergebnis-Wege, nicht nur für „Bestanden": der Server
  // prüft den Vorgänger VOR dem Ergebnis (routes/runs.ts) und weist `error` und `blocked` genauso
  // mit 409 ab. `needsResultText` dagegen betrifft nur den grünen Lauf — deshalb hier eine eigene
  // Bedingung und nicht `passBlockedReason`.
  const runBlocked = needsChainPick || Boolean(predecessorBlocked);
  // #186: Wortlaut der Hinweise unter den Schaltflächen hängt an der Kette, die der Lauf
  // TATSÄCHLICH trägt (`effectiveE2eId`), nicht am Katalog-Badge: im „nur Prozesse"-Plan (#127) ist
  // sie null, dort gibt es keine Kette, die anhalten könnte.
  const inChainRun = effectiveE2eId !== null;
  // #186: „Testfall bestanden" gibt den Lauf als bestanden ab — das Ergebnis geht dann an den
  // NÄCHSTEN Schritt (die Beschreibung ist sein Übergabetext). Hat das Glied einen Nachfolger, ist
  // sie deshalb Pflicht, genau wie das Übergabefeld beim grünen Lauf (A3). Der Server erzwingt es
  // ebenfalls (400 `result_description_required`).
  const effectiveStopsChain = linkedFixed ? 'yes' : stopsChain;
  const errorNeedsDescription =
    effectiveStopsChain === 'no' && hasSuccessor && !resultDescription.trim();
  // #187: Aus einer Keyuser-Schulung — die Schritt-Pflicht hängt an der Schrittzahl. Genau ein
  // Schritt (der scenario_flow-Import legt genau einen an) steht fest und wird mitgeschickt; ohne
  // Schritt war der Dialog eine Sackgasse (Pflichtfeld ohne Auswahl), dort entfällt das Feld und es
  // geht null raus. Nur bei mehreren Schritten bleibt die Wahl Pflicht.
  const orderedSteps = (tcQuery.data?.steps ?? []).slice().sort((a, b) => a.step_no - b.step_no);
  const fixedStep = orderedSteps.length === 1 ? orderedSteps[0] : undefined;
  const stepRequired = orderedSteps.length > 1;
  const errorSaveBlocked =
    notedUnavailable ||
    nothingChosen ||
    (newPartActive && stepRequired && !failedStepId) ||
    effectiveStopsChain === null ||
    errorNeedsDescription;
  // #186: Schlüssel des Hinweistextes unter der Schaltfläche — Ergebnis x Kette/Einzeltest.
  const hintKey = (outcome: 'failed' | 'passed') =>
    `exec.stopsChain.hint${outcome === 'failed' ? 'Failed' : 'Passed'}${inChainRun ? 'Chain' : 'Standalone'}`;

  const submitRun = useMutation({
    mutationFn: (body: RunSubmitBody) => runsApi.submitRun(projectId, planId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['playlist', projectId, planId] });
      // #208: Nach jedem gebuchten Lauf beginnt die Liste notierter Fehler neu (Grenze = letzter Lauf).
      void queryClient.invalidateQueries({ queryKey: ['noted-step-defects', projectId, planId] });
      // #126/#186: ein „bestanden, Fehler gemeldet" legt einen Defect an und ändert das Glied in
      // der Kettensicht (Hinweis „Testfall bestanden, Fehler … gemeldet") — beide Sichten sind
      // danach veraltet. Prefix ohne `e2e_id`: es können mehrere Ketten des Plans geladen sein.
      void queryClient.invalidateQueries({ queryKey: ['e2e-chain', projectId, planId] });
      void queryClient.invalidateQueries({ queryKey: ['my-defects', projectId] });
      // #208 Nachtrag: verlinkte notierte Fehler hängen jetzt am Lauf — „Von mir gemeldet" zeigt sie
      // mit dem Lauftext.
      void queryClient.invalidateQueries({ queryKey: ['my-reported', projectId] });
      // #193: Nach einem Lauf gehören die bisherigen Kommentare zu „Aus vorherigen Testläufen" —
      // das urteilt der Server per Zeitstempel, der Client lädt nur neu. Prefix ohne Testfall: der
      // Auto-Sprung wechselt gleich den Testfall, und die Liste des alten soll nicht veraltet im
      // Cache stehen bleiben.
      void queryClient.invalidateQueries({ queryKey: ['execution-comments', projectId, planId] });
      // #195: Die Weitergabe-Übersicht (`mine`) hängt am Lauf-Stand: „Fehler" nimmt die Karte aus
      // dem Abgeben, ein frischer Retest bringt sie hinein. Ohne Reload bliebe das Kästchen stehen
      // und die Weitergabe endete als „0 von 1".
      void queryClient.invalidateQueries({ queryKey: ['handover', projectId, planId] });
      // #208: Der Server löscht beim gebuchten Ergebnis (jedes Ergebnis, nicht nur `successful`)
      // alle Haken des Testfalls im Plan — die Regression beginnt leer. Prefix ohne Testfall: der
      // Auto-Sprung wechselt gleich den Testfall.
      void queryClient.invalidateQueries({ queryKey: ['step-checks', projectId, planId] });
      onSubmitted();
    },
  });

  const addScreenshot = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') return;
        if (dataUrl.length > SCREENSHOT_MAX_BYTES) {
          setScreenshotError(t('exec.screenshotTooLarge'));
          return;
        }
        setScreenshotError(null);
        setScreenshots((prev) => [...prev, dataUrl]);
      };
      reader.readAsDataURL(file);
    },
    [t],
  );

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (file) addScreenshot(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) addScreenshot(file);
  };

  const removeScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleTestData = (id: string) => {
    setSelectedTestDataIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handlePass = () => {
    // #185: keine Tastatur-Abkürzung mehr — in der ETO-Schulung wurden Tests versehentlich per
    // Enter als bestanden gebucht. `passBlockedReason` bleibt trotzdem im Guard und nicht nur am
    // `disabled` des Knopfes: so gilt der Riegel für jeden Aufrufer von `handlePass`.
    if (!testCaseId || submitRun.isPending || passBlockedReason) return;
    submitRun.mutate({
      test_case_id: testCaseId,
      result: 'successful',
      // A3: der Übergabetext. Ohne Kette ist das Feld gar nicht da, `chainResult` also leer → null.
      result_description: chainResult.trim() || null,
      e2e_id: effectiveE2eId,
      used_test_data_ids: selectedTestDataIds,
      screenshots,
    });
  };

  const handleBlocked = () => {
    // Der Ergebnistext ist bei `blocked` optional (A3) — mitgeschickt wird er trotzdem, sonst wäre
    // das sichtbare Feld beim blockierten Lauf ein Feld ohne Konsequenz. Die Ketten-Auswahl bleibt
    // auch hier Pflicht: ohne sie antwortet der Server mit 422. `runBlocked` gehört wie bei
    // `handlePass` in den Guard und nicht nur an das `disabled` des Knopfes.
    if (!testCaseId || runBlocked) return;
    submitRun.mutate({
      test_case_id: testCaseId,
      result: 'blocked',
      result_description: chainResult.trim() || null,
      e2e_id: effectiveE2eId,
      used_test_data_ids: selectedTestDataIds,
      screenshots,
    });
  };

  const handleSkip = () => {
    onSubmitted();
  };

  const submitError = (e: FormEvent) => {
    e.preventDefault();
    if (!testCaseId || errorSaveBlocked || runBlocked) return;
    submitRun.mutate(
      {
        test_case_id: testCaseId,
        // #186: „Testfall nicht bestanden" → Lauf `error`. „Testfall bestanden" = der Fehler wird
        // gemeldet, der Testfall gilt trotzdem als bestanden → Lauf `successful` MIT Defect
        // („bestanden, Fehler gemeldet"); der Server baut den Defect aus failed_step_id,
        // Beschreibung, Screenshots und Severity. `stops_chain` geht mit, damit er Ergebnis und
        // Antwort gegeneinander prüft.
        // #208 Nachtrag: mit angehaktem notiertem Fehler fest „nicht bestanden" (Entscheidung 4).
        result: effectiveStopsChain === 'no' ? 'successful' : 'error',
        stops_chain: effectiveStopsChain === 'yes',
        // Beim Fehler ist die Fehlerbeschreibung der Ergebnistext — der Übergabetext des grünen
        // Laufs gehört nicht in einen Lauf, der gar nichts übergibt (eine Spalte, ein Ergebnis).
        // Bei „Testfall bestanden" wird sie zugleich zum Übergabetext des Gliedes (eine
        // Beschreibung für beides).
        result_description: resultDescription.trim() || null,
        // #208 Nachtrag: Schritt und Severity gehören zum NEUEN Fehler — ohne „neu" null, auch
        // der feste Schritt im Ein-Schritt-Fall.
        failed_step_id: newPartActive
          ? (fixedStep?.step_id ?? (stepRequired ? failedStepId || null : null))
          : null,
        e2e_id: effectiveE2eId,
        used_test_data_ids: selectedTestDataIds,
        screenshots,
        severity_id: newPartActive ? severityId || null : null,
        // Nur mit notierten Fehlern: ohne sie bleibt der Body exakt wie bisher (alte Semantik).
        ...(hasNoted ? { linked_defect_ids: linkedIds, create_defect: createNew } : {}),
      },
      {
        onSuccess: () => setErrorDialogOpen(false),
        // #208 Nachtrag: Ein notierter Fehler ist inzwischen verknüpft oder weg (422/409) — die
        // Liste neu laden, damit die Auswahl den Serverstand zeigt.
        onError: (err) => {
          if (isLinkedDefectError(err)) {
            void queryClient.invalidateQueries({
              queryKey: ['noted-step-defects', projectId, planId],
            });
          }
        },
      },
    );
  };

  if (!testCaseId) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-text-muted">
        {t('exec.selectPrompt')}
      </div>
    );
  }

  if (tcQuery.isLoading || !tcQuery.data) {
    return <div className="p-6 text-text-muted">{t('catalog.loading')}</div>;
  }

  const tc = tcQuery.data;
  const testData = testDataQuery.data ?? [];
  const kindsById = new Map((dataKindsQuery.data ?? []).map((k) => [k.data_kind_id, k]));
  // #179: Anzeigetexte nach Oberflächensprache, Rückfall auf die andere Sprache.
  const lang = i18n.resolvedLanguage ?? 'de';
  const title = pickLang(lang, tc.title_de, tc.title_en);
  const precondition = pickLang(lang, tc.precondition_de, tc.precondition_en);
  const expected = pickLang(lang, tc.expected_de, tc.expected_en);
  // #90: sort_order wie im Board (components/board.tsx) — die Reihenfolge des Admin-Vokabulars.
  const orderedSeverities = (defectConfigQuery.data?.severity_levels ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    // Paste-Zone muss den ganzen Panel-Bereich (inkl. Fehler-Dialog, kein Portal) abdecken,
    // damit Strg+V unabhängig vom Fokus-Ort greift (EXE-6).
    <div onPaste={handlePaste} className="space-y-4 p-6">
      {/* A1: Die Kettenleiste steht ÜBER dem Testfall — „wer war vor mir dran, was ist dabei
          entstanden". Der kettenlose Fall wird hier weggeschnitten, die Leiste bekommt beide IDs
          deshalb nicht-nullable. */}
      {effectiveE2eId && (
        <ChainBar
          projectId={projectId}
          planId={planId}
          e2eId={effectiveE2eId}
          testCaseId={testCaseId}
          canRelease={canRelease}
        />
      )}

      {/* A6/Review-Befund Important 1: sichtbar, solange die Auswahl selbst keine Kette nennt UND
          der Testfall in mehreren liegt (`showChainPick`) — bei genau einer Mitgliedschaft ist
          `effectiveE2eId` bereits belegt, es gibt nichts zu wählen. Bewusst NICHT an
          `needsChainPick` gehängt: die wird mit der ersten gültigen Wahl `false`, die Liste bliebe
          aber gerade dann verschwunden, wenn der Tester sie zum Korrigieren bräuchte. Die Wahl
          bleibt also stehen — nur das ABSENDEN sperrt an `needsChainPick`, solange keine gültige
          Wahl vorliegt. */}
      {showChainPick && (
        <div className="space-y-1 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <label className="block text-xs text-text-muted" htmlFor="exec-chain-pick">
            {t('exec.chain.pickChain')}
          </label>
          <select
            id="exec-chain-pick"
            value={validPick}
            onChange={(e) => setPickedE2eId(e.target.value)}
            className="w-full max-w-md rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
          >
            <option value="">{t('exec.chain.pickChainPlaceholder')}</option>
            {memberships.map((m, i) => {
              const view = chainNameQueries[i]?.data;
              return (
                <option key={m.e2e_id} value={m.e2e_id}>
                  {view
                    ? // Review-Befund Minor 2: `name_en` ist notNull().default('') — eine
                      // unuebersetzte Kette liefert '' und stuende hier ohne Namen da.
                      `${view.human_id} · ${pickLang(i18n.resolvedLanguage, view.name_de, view.name_en)}`
                    : t('catalog.loading')}
                </option>
              );
            })}
          </select>
        </div>
      )}

      <div className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <div className="flex flex-wrap items-center gap-3">
          <IdLabel id={tc.human_id} />
          {tc.e2e_memberships.length > 0 && <E2EBadge />}
          {/* #211 Nachtrag: der Prozess des Testfalls. Bei Einzeltests stand er sonst nirgends im
              Ausführungsfeld (Ketten zeigen darüber die Kettenleiste). Quelle ist die Herkunft am
              Testfall-Detail (`origin.prozess`), dieselbe wie in der Katalog-Detailansicht. */}
          {tc.origin?.prozess && (
            <span className="text-xs text-text-muted">
              {t('exec.processLabel')} ·{' '}
              {pickLang(lang, tc.origin.prozess.name_de, tc.origin.prozess.name_en)}
            </span>
          )}
          <details className="ml-auto text-sm">
            <summary className="cursor-pointer rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-text-body">
              {t('exec.testData')} ({selectedTestDataIds.length})
            </summary>
            <div className="mt-2 max-h-48 w-64 overflow-y-auto rounded-btn border border-[var(--border)] bg-bg-surface p-2">
              {testData.length === 0 && (
                <span className="text-xs text-text-muted">{t('catalog.empty')}</span>
              )}
              {testData.map((row) => {
                const kind = kindsById.get(row.data_kind_id);
                const label = kind ? kindLabel(kind, lang) : '—';
                return (
                  <label
                    key={row.test_data_id}
                    className="flex items-center gap-2 py-1 text-xs text-text-body"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTestDataIds.includes(row.test_data_id)}
                      onChange={() => toggleTestData(row.test_data_id)}
                    />
                    {/* #95: Checkbox und Text sind Geschwister im <label> — die Checkbox liegt
                        AUSSERHALB des ClipTooltip-Wrappers, ihr Fokus wuerde also nicht in ihn
                        hineinbubbeln. tabIndex macht den Text selbst zum Fokus-Ziel, ohne die
                        Checkbox-Semantik anzufassen. */}
                    <ClipTooltip text={`${label}: ${row.value}`} className="min-w-0 flex-1">
                      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                          eigenes Klickziel — der Tab-Stop existiert nur fuer die
                          ClipTooltip-Blase (#95). */}
                      <span data-clip tabIndex={0} className="block truncate">
                        {label}: {row.value}
                      </span>
                    </ClipTooltip>
                  </label>
                );
              })}
            </div>
          </details>
        </div>

        <h2 className="text-lg font-bold text-text-primary">{title}</h2>

        <div className="flex flex-wrap gap-2">
          {precondition && (
            <span className="rounded-chip border border-[var(--border)] px-2 py-1 text-xs text-text-body">
              {t('catalog.fields.precondition')}: {precondition}
            </span>
          )}
          {expected && (
            <span className="rounded-chip border border-[var(--border)] px-2 py-1 text-xs text-text-body">
              {t('catalog.fields.expected')}: {expected}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
        {orderedSteps.length === 0 ? (
          <div className="text-sm text-text-muted">{t('catalog.empty')}</div>
        ) : (
          // #208: Input-Spalte, Haken je Schritt, Defect je Schritt, Abschluss-Frage. „Ja" geht
          // über `handlePass` und ist mit demselben Grund gesperrt wie der Bestanden-Knopf.
          <StepChecklist
            projectId={projectId}
            planId={planId}
            testCaseId={testCaseId}
            testCaseHumanId={tc.human_id}
            steps={orderedSteps}
            lang={lang}
            canWrite={!runBlocked}
            passBlockedReason={passBlockedReason}
            onConfirmPass={handlePass}
          />
        )}
      </div>

      {/* #27 (Finding 27): genau EINE Zone, und zwar dort, wo der Nutzer gerade ist. Bei offenem
          Fehler-Dialog wandert sie in den Dialog (unten) — vorher lag sie unter dem Overlay und
          Vorschau wie Entfernen-Button waren unerreichbar. Zwei Zonen gleichzeitig waeren
          verwirrend: derselbe Zustand an zwei Stellen bedienbar. */}
      {/* A3: „Ergebnis für die nächsten Schritte" — NUR bei Kettenmitgliedschaft. Bei einem
          Testfall ohne Kette erscheint es gar nicht; dort reicht weiterhin „hat funktioniert laut
          Tester". Es steht über der Bildzone und damit direkt über der Aktionsleiste: beides
          zusammen ist das, was der Tester weitergibt. */}
      {inChain && (
        <div className="space-y-1">
          <label className="block text-xs text-text-muted" htmlFor="exec-chain-result">
            {t('exec.chain.resultLabel')}
            {hasSuccessor && <span className="text-status-fail"> *</span>}
          </label>
          <textarea
            id="exec-chain-result"
            rows={2}
            value={chainResult}
            onChange={(e) => setChainResult(e.target.value)}
            placeholder={t('exec.chain.resultPlaceholder')}
            className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
          />
        </div>
      )}

      {!errorDialogOpen && (
        <ScreenshotZone
          screenshots={screenshots}
          error={screenshotError}
          onDrop={handleDrop}
          onRemove={removeScreenshot}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handlePass}
          disabled={submitRun.isPending || Boolean(passBlockedReason)}
          aria-describedby={passBlockedReason ? 'exec-pass-blocked' : undefined}
          className="flex items-center gap-2 rounded-btn bg-status-pass px-5 py-2.5 text-base font-bold text-white disabled:opacity-60"
        >
          ✓ {t('exec.pass')}
        </button>
        <button
          type="button"
          onClick={() => {
            // #90: Reset beim Öffnen — ein zuvor gewählter Vorschlag darf nicht in eine neue
            // Fehlermeldung durchsickern (der Dialog-State selbst überlebt das Schließen, s.
            // failedStepId/resultDescription oben).
            setSeverityId('');
            // #186: dasselbe für die Pflichtwahl — eine frühere Wahl darf nicht in eine neue
            // Fehlermeldung durchsickern.
            setStopsChain(null);
            // #208 Nachtrag: Auswahl der notierten Fehler startet wieder bei „alle, nichts Neues".
            setUnlinkedIds([]);
            setCreateNew(false);
            setErrorDialogOpen(true);
          }}
          // Ohne gewählte Kette bleibt auch dieser Weg zu: der Dialog liesse sich sonst ausfüllen
          // und das Speichern täte wortlos nichts (`submitError` steigt bei `needsChainPick` aus).
          // Dasselbe bei offenem Vorgänger — dort weist der Server auch den Fehler-Lauf mit 409 ab.
          disabled={submitRun.isPending || runBlocked}
          className="rounded-btn border border-status-fail px-3 py-2 text-sm text-status-fail hover:bg-status-fail/10 disabled:opacity-60"
        >
          ✕ {t('exec.fail')}
        </button>
        <button
          type="button"
          onClick={handleBlocked}
          disabled={submitRun.isPending || runBlocked}
          className="rounded-btn border border-[var(--border)] px-3 py-2 text-sm text-text-body hover:bg-[var(--bg-subtle)] disabled:opacity-60"
        >
          {t('exec.blocked')}
        </button>
        <button
          type="button"
          onClick={handleSkip}
          className="px-3 py-2 text-sm text-text-muted hover:text-text-body"
        >
          {t('exec.skip')}
        </button>
        <span className="text-xs text-text-muted">{t('exec.autoJumpHint')}</span>
      </div>

      {/* „Absenden gesperrt" ohne genannten Grund ist eine Sackgasse — der Tester sieht einen
          grauen Knopf und weiß nicht, was ihm fehlt. */}
      {passBlockedReason && (
        <p id="exec-pass-blocked" className="text-xs text-status-fail">
          {passBlockedReason}
        </p>
      )}

      {/* Task 9: Die Antwort des Servers wird angezeigt, nicht verschluckt. Bis hierher endete
          jeder abgewiesene Lauf still — der Tester klickte „Passt", nichts geschah, und der Grund
          (409 Sperre, 400 Pflichttext, 422 fehlende Kette) blieb in der Konsole. */}
      {/* #208 Nachtrag: die Meldungen zu notierten Fehlern stehen bei offenem Dialog dort, nicht doppelt. */}
      {submitRun.isError && !(errorDialogOpen && isLinkedDefectError(submitRun.error)) && (
        <p role="alert" className="text-sm text-status-fail">
          {mapApiError(t, submitRun.error)}
        </p>
      )}

      {/* #193: Kommentar zur Ausführung — direkt unter der Aktionsleiste, aber getrennt vom
          Ergebnis: ein Kommentar ist kein Lauf, er schließt den Testfall nicht ab und sperrt weder
          Passt/Fehler/Blockiert noch den Auto-Sprung. Nur mit Plan und Testfall (die Playlist
          reicht ohne gewählten Plan '' durch). Der Testfallwechsel remountet das Panel per `key`,
          die Eingabe wird also nicht in einen anderen Testfall mitgenommen. */}
      {planId && testCaseId && (
        <ExecutionComments projectId={projectId} planId={planId} testCaseId={testCaseId} />
      )}

      {errorDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <form
            onSubmit={submitError}
            className="max-h-[90vh] w-[34rem] max-w-[calc(100vw-2rem)] space-y-3 overflow-y-auto rounded-card border border-[var(--border)] bg-bg-surface p-5 shadow-lg"
          >
            <h3 className="text-sm font-bold text-text-primary">{t('exec.fail')}</h3>
            {/* #208 (Final-Review 2): Speichern ist gesperrt, solange die notierten Fehler laden oder
                nicht ladbar waren — der Grund steht sichtbar da, bei Fehler mit „Erneut versuchen". */}
            {notedLoading && <p className="text-xs text-text-muted">{t('exec.noted.loading')}</p>}
            {notedFailed && <QueryErrorNotice compact onRetry={() => notedQuery.refetch()} />}
            {/* #208 Nachtrag (Entscheidung 2): nur mit notierten Fehlern — sonst wie heute. */}
            {hasNoted && (
              <NotedDefectsChoice
                defects={notedDefects}
                selectedIds={linkedIds}
                onToggle={(id) =>
                  setUnlinkedIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
                createNew={createNew}
                onCreateNewChange={setCreateNew}
                lang={lang}
              />
            )}
            {linkedFixed && (
              <p className="rounded-btn bg-bg-subtle px-2 py-1.5 text-[11px] text-text-muted">
                {t('exec.noted.failedFixed')}
              </p>
            )}
            {/* #208 Nachtrag: Die Bestanden-Wahl gibt es nur, solange kein notierter Fehler angehakt
                ist und ein neuer Fehler gemeldet wird (ohne notierte Fehler: immer, wie heute). */}
            {/* #186: zwei Schaltflächen statt Ja/Nein (doppelte Verneinung: „Hält der Fehler an? —
                Nein" verwechselten Tester in der Schulung). Die Wahl entscheidet, was der Lauf
                überhaupt ist (nicht bestanden oder „bestanden, Fehler gemeldet"); keine
                Vorauswahl, solange nichts gewählt ist, bleibt Speichern gesperrt. Echte
                Radio-Eingaben (visuell versteckt, Farbfüllung über `peer-checked`) statt
                `<button role="radio">` — Biome a11y/useSemanticElements verbietet Letzteres, und
                ein Radio schickt das Formular nicht ab. Der Hinweis steht immer unter seiner
                Schaltfläche: die Folge soll VOR dem Klick lesbar sein. */}
            {!linkedFixed && newPartActive && (
              <div className="space-y-1">
                <p id="exec-stops-chain-question" className="text-xs font-medium text-text-body">
                  {t('exec.stopsChain.question')}
                  <span className="text-status-fail"> *</span>
                </p>
                <div
                  role="radiogroup"
                  aria-labelledby="exec-stops-chain-question"
                  aria-required="true"
                  className="grid grid-cols-2 gap-3"
                >
                  {(
                    [
                      {
                        answer: 'yes',
                        outcome: 'failed',
                        tone: 'border-status-fail text-status-fail hover:bg-status-fail/10 peer-checked:bg-status-fail peer-checked:text-white',
                      },
                      {
                        answer: 'no',
                        outcome: 'passed',
                        tone: 'border-status-pass text-status-pass hover:bg-status-pass/10 peer-checked:bg-status-pass peer-checked:text-white',
                      },
                    ] as const
                  ).map(({ answer, outcome, tone }) => (
                    <div key={outcome}>
                      <label className="block cursor-pointer">
                        <input
                          type="radio"
                          name="exec-stops-chain"
                          value={answer}
                          checked={stopsChain === answer}
                          onChange={() => setStopsChain(answer)}
                          aria-describedby={`exec-outcome-${outcome}-hint`}
                          className="peer sr-only"
                        />
                        <span
                          className={`block w-full rounded-btn border-2 px-4 py-3 text-center text-sm font-bold peer-focus-visible:ring-2 peer-focus-visible:ring-brand-accent ${tone}`}
                        >
                          {t(`exec.stopsChain.${outcome}`)}
                        </span>
                      </label>
                      <p
                        id={`exec-outcome-${outcome}-hint`}
                        className="mt-1 text-[11px] text-text-muted"
                      >
                        {t(hintKey(outcome))}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Whole-Branch-Review, Minor 2: Der Übergabetext geht beim Fehler-Lauf verloren — und
                bis hierher sagte das niemand. Das Feld „Ergebnis für die nächsten Schritte" steht
                bei jedem Kettenglied sichtbar über der Aktionsleiste; wer es tippt und dann
                „✕ Fehler melden" drückt, ist ihn ersatzlos los (`submitError` schickt bewusst die
                Fehlerbeschreibung als `result_description` — ein Lauf, der nichts übergibt, trägt
                keinen Übergabetext).
                HINWEIS statt Ausblenden: solange dieser Dialog offen ist (fixed inset-0), liegt das
                Feld ohnehin unter dem Overlay — es dort zu verstecken sähe niemand. Der Satz steht
                deshalb an der Stelle, an der der Tester gerade hinsieht. Und nur, wenn wirklich
                Text dasteht: an einem leeren Feld wäre er reines Rauschen. */}
            {inChain && chainResult.trim() !== '' && (
              <p className="rounded-btn bg-bg-subtle px-2 py-1.5 text-[11px] text-text-muted">
                {t('exec.chain.resultNotKeptOnError')}
              </p>
            )}
            {!newPartActive ? null : stepRequired ? (
              <div className="space-y-1">
                <label className="block text-xs text-text-muted" htmlFor="exec-failed-step">
                  {t('exec.failedStep')}
                </label>
                <select
                  id="exec-failed-step"
                  required
                  value={failedStepId}
                  onChange={(e) => setFailedStepId(e.target.value)}
                  className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
                >
                  <option value="">{t('exec.selectStep')}</option>
                  {orderedSteps.map((step) => (
                    <option key={step.step_id} value={step.step_id}>
                      #{step.step_no} {pickLang(lang, step.description_de, step.description_en)}
                    </option>
                  ))}
                </select>
              </div>
            ) : fixedStep ? (
              <div className="space-y-1">
                <span className="block text-xs text-text-muted">{t('exec.failedStep')}</span>
                <p className="text-sm text-text-body">
                  #{fixedStep.step_no}{' '}
                  {pickLang(lang, fixedStep.description_de, fixedStep.description_en)}
                </p>
              </div>
            ) : null}
            {newPartActive && defectConfigQuery.isError ? (
              <QueryErrorNotice compact onRetry={() => defectConfigQuery.refetch()} />
            ) : null}
            {newPartActive && (
              <div className="space-y-1">
                <label className="block text-xs text-text-muted" htmlFor="exec-severity">
                  {t('exec.severityLabel')}
                </label>
                <select
                  id="exec-severity"
                  aria-label={t('exec.severityLabel')}
                  value={severityId}
                  onChange={(e) => setSeverityId(e.target.value)}
                  className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
                >
                  <option value="">{t('exec.severityDefault')}</option>
                  {orderedSeverities.map((s) => (
                    <option key={s.severity_id} value={s.severity_id}>
                      {pickLang(lang, s.label_de, s.label_en)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* #208 Nachtrag: Die Beschreibung bleibt auch ohne „neu" sichtbar — dann ist sie der
                (optionale) Lauftext neben den verknüpften notierten Fehlern. */}
            {!nothingChosen && (
              <div className="space-y-1">
                <label className="block text-xs text-text-muted" htmlFor="exec-error-description">
                  {t('exec.description')}
                  {effectiveStopsChain === 'no' && hasSuccessor && (
                    <span className="text-status-fail"> *</span>
                  )}
                </label>
                <textarea
                  id="exec-error-description"
                  rows={6}
                  value={resultDescription}
                  onChange={(e) => setResultDescription(e.target.value)}
                  className="min-h-[8rem] w-full resize-y rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
                />
                {/* #186: bei „Testfall bestanden" mit Nachfolger IST die Beschreibung der Übergabetext
                  — sagen, damit Belegnummern dort landen und nicht im Fehlertext verloren gehen. */}
                {effectiveStopsChain === 'no' && hasSuccessor && (
                  <p className="text-[11px] text-text-muted">
                    {t('exec.stopsChain.descriptionIsHandover')}
                  </p>
                )}
              </div>
            )}
            {/* #27 (Finding 27): die volle Zone statt nur einer Zaehl-Zeile. Die Vorschau und die
                Entfernen-Buttons standen bisher ausschliesslich im Panel dahinter — sobald dieser
                Dialog offen war (fixed inset-0 z-50), lagen sie unter dem Overlay und waren weder
                sicht- noch klickbar. Genau hier legt der Tester seine Screenshots an. */}
            <ScreenshotZone
              screenshots={screenshots}
              error={screenshotError}
              onDrop={handleDrop}
              onRemove={removeScreenshot}
            />
            {/* #132: Dezenter Hinweis auf Screenshots ohne Erzwingung — verschwindet,
                sobald ein Bild angehängt ist. */}
            {screenshots.length === 0 && (
              <p className="rounded-btn bg-bg-subtle px-2 py-1.5 text-[11px] text-text-muted">
                {t('exec.screenshotHintError')}
              </p>
            )}
            {/* #208 Nachtrag: 422/409 zu notierten Fehlern IM Dialog zeigen — die Meldung unter der
                Aktionsleiste liegt unter dem Overlay. Andere Fehler bleiben dort (wie bisher). */}
            {submitRun.isError && isLinkedDefectError(submitRun.error) && (
              <p role="alert" className="text-xs text-status-fail">
                {mapApiError(t, submitRun.error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setErrorDialogOpen(false);
                  // #208 (Final-Review 2): Ein 422/409 zu notierten Fehlern gehört zum Dialog — nicht
                  // unter die Aktionsleiste, wenn der Dialog abgebrochen wird.
                  submitRun.reset();
                }}
                className="px-3 py-1.5 text-sm text-text-muted"
              >
                {t('catalog.actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={errorSaveBlocked || submitRun.isPending}
                className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
              >
                {t('catalog.actions.save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
